// Gerenciar escolas de origem (Novos DNAs) — unir aliases e corrigir o nome oficial.
// O texto digitado pela família (school_origin_raw) nunca é alterado.
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function SchoolOriginsManager({ onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [names, setNames] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/prospects/schools/origins`, { headers: headers() });
      setItems(data.items || []);
      setNames(Object.fromEntries((data.items || []).map((i) => [i.id, i.name])));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar escolas de origem");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rebuild = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/prospects/schools/rebuild`, {}, { headers: headers() });
      toast.success(`${data.schools} escola(s) · ${data.variants} grafia(s) · ${data.ai_merged} unida(s) pela IA`);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao recalcular");
    } finally { setBusy(false); }
  };

  const salvarNome = async (id) => {
    const name = (names[id] || "").trim();
    const atual = items.find((i) => i.id === id);
    if (!name || name === atual?.name) return;
    try {
      await axios.put(`${API}/prospects/schools/${id}`, { name }, { headers: headers() });
      toast.success("Nome oficial atualizado");
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao renomear"); }
  };

  const unir = async () => {
    const sources = [...picked].filter((id) => id !== target);
    if (!target || sources.length === 0) { toast.error("Escolha a escola oficial e as grafias a unir."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/prospects/schools/merge`, { target_id: target, source_ids: sources },
        { headers: headers() });
      toast.success(`${sources.length} escola(s) unida(s)`);
      setPicked(new Set()); await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao unir escolas");
    } finally { setBusy(false); }
  };

  const revisado = async (id) => {
    try {
      await axios.put(`${API}/prospects/schools/${id}`, { needs_review: false }, { headers: headers() });
      await load(); onChanged?.();
    } catch (e) { toast.error("Erro ao marcar como revisada"); }
  };

  const toggle = (id) => setPicked((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  return (
    <div style={S.backdrop} onClick={onClose} data-testid="school-origins-manager">
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={S.head}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1c1230" }}>Escolas de origem</div>
            <div style={{ fontSize: 12, color: "#6b6483" }}>
              O texto digitado pela família é preservado; aqui você define o nome oficial usado
              em filtros, gráficos e métricas.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={rebuild} disabled={busy} style={S.primary} data-testid="school-rebuild">
              {busy ? "Processando..." : "Recalcular (IA)"}
            </button>
            <button onClick={onClose} style={S.ghost} data-testid="school-close">Fechar</button>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0" }}>
          <button onClick={unir} disabled={busy || !target || picked.size === 0}
            style={{ ...S.primary, opacity: !target || picked.size === 0 ? 0.5 : 1 }}
            data-testid="school-merge">
            Unir selecionadas na oficial ({[...picked].filter((i) => i !== target).length})
          </button>
          <span style={{ fontSize: 12, color: "#6b6483" }}>
            Marque a coluna “Oficial” no nome correto e selecione as grafias equivalentes.
          </span>
        </div>

        {loading ? (
          <div style={S.empty}>Carregando...</div>
        ) : items.length === 0 ? (
          <div style={S.empty}>Nenhuma escola de origem registrada. Use “Recalcular (IA)”.</div>
        ) : (
          <div style={{ overflow: "auto", maxHeight: "60vh" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Oficial</th>
                  <th style={S.th}>Unir</th>
                  <th style={S.th}>Nome padronizado</th>
                  <th style={S.th}>Grafias digitadas (originais)</th>
                  <th style={S.th}>Famílias</th>
                  <th style={S.th}>Alunos</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} style={S.tr}>
                    <td style={S.td}>
                      <input type="radio" name="school-target" checked={target === i.id}
                        onChange={() => setTarget(i.id)} data-testid={`school-target-${i.id}`} />
                    </td>
                    <td style={S.td}>
                      <input type="checkbox" checked={picked.has(i.id)} onChange={() => toggle(i.id)}
                        data-testid={`school-pick-${i.id}`} />
                    </td>
                    <td style={S.td}>
                      <input value={names[i.id] ?? i.name}
                        onChange={(e) => setNames((n) => ({ ...n, [i.id]: e.target.value }))}
                        onBlur={() => salvarNome(i.id)} style={S.input}
                        data-testid={`school-name-${i.id}`} />
                      {i.needs_review && (
                        <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={S.reviewChip} data-testid={`school-review-${i.id}`}>revisar</span>
                          <span style={{ fontSize: 11, color: "#92400e" }}>{i.review_note}</span>
                          <button onClick={() => revisado(i.id)} style={S.link}
                            data-testid={`school-review-ok-${i.id}`}>ok, é diferente</button>
                        </div>
                      )}
                    </td>
                    <td style={{ ...S.td, fontSize: 12, color: "#6b6483" }}>
                      {(i.aliases || []).join(" · ")}
                    </td>
                    <td style={S.td}>{i.families}</td>
                    <td style={S.td}>{i.students}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(24,12,45,.55)", zIndex: 1200,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  sheet: { background: "#fff", borderRadius: 16, padding: 18, width: "min(1000px, 100%)",
    boxShadow: "0 24px 60px rgba(28,18,48,.28)" },
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
    borderBottom: "1px solid #f1ecfa", paddingBottom: 12 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: .4,
    color: "#8b82a6", padding: "8px 10px", borderBottom: "1px solid #f1ecfa", whiteSpace: "nowrap" },
  td: { padding: "10px", borderBottom: "1px solid #faf7ff", fontSize: 13, color: "#2d1f4a", verticalAlign: "top" },
  tr: { background: "#fff" },
  input: { padding: "6px 10px", borderRadius: 8, border: "1.5px solid #ede7f7", fontSize: 13,
    outline: "none", width: 240, fontWeight: 700, color: "#3f2a6b" },
  primary: { padding: "8px 14px", borderRadius: 10, border: "none", background: "#7B3F98",
    color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  ghost: { padding: "8px 14px", borderRadius: 10, border: "1.5px solid #ede7f7",
    background: "#fff", color: "#3F1F5C", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  link: { border: "none", background: "transparent", color: "#7B3F98", fontSize: 11,
    fontWeight: 700, cursor: "pointer", padding: 0 },
  reviewChip: { background: "#fef3c7", color: "#92400e", borderRadius: 999, padding: "1px 8px",
    fontSize: 11, fontWeight: 800 },
  empty: { padding: 24, textAlign: "center", color: "#8b82a6", fontSize: 13 },
};
